import api from "./api";

let cached = null;

async function getConfig() {
  if (cached) return cached;
  try {
    const [media, mux] = await Promise.all([
      api.get("/media/config"),
      api.get("/mux/config"),
    ]);
    cached = { ...media.data, ...mux.data };
  } catch {
    cached = { cloudinary_enabled: false, mux_enabled: false };
  }
  return cached;
}

function muxDirectPut(file, url, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && onProgress) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Mux upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error("Mux upload network error"));
    xhr.send(file);
  });
}

/**
 * Upload a media file.
 *  - Videos → Mux Direct Upload (HLS streaming + CDN). Falls back to Cloudinary then Emergent storage.
 *  - Images → Cloudinary signed upload. Falls back to Emergent storage.
 * Returns { path, media_type, mux?: { upload_id, passthrough }, aspect_ratio? }.
 */
export async function uploadMedia(file, { onProgress } = {}) {
  const cfg = await getConfig();
  const isVideo = (file.type || "").startsWith("video");
  const resourceType = isVideo ? "video" : "image";

  // ---------- Video via Mux ----------
  if (isVideo && cfg.mux_enabled) {
    const { data } = await api.post("/mux/create-upload", {
      cors_origin: window.location.origin,
    });
    await muxDirectPut(file, data.url, onProgress);
    return {
      path: `mux://${data.passthrough}`,
      media_type: "video",
      mux: {
        upload_id: data.upload_id,
        passthrough: data.passthrough,
        status: "processing",
      },
    };
  }

  // ---------- Image (or video fallback) via Cloudinary ----------
  if (cfg.cloudinary_enabled) {
    const { data: sig } = await api.get(`/cloudinary/signature?resource_type=${resourceType}`);
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
      { method: "POST", body: form },
    );
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`Cloudinary upload failed: ${t}`);
    }
    const r = await res.json();
    await api.post("/cloudinary/register", {
      public_id: r.public_id,
      secure_url: r.secure_url,
      resource_type: resourceType,
      bytes: r.bytes,
      format: r.format,
    });
    return { path: r.secure_url, media_type: isVideo ? "video" : "image" };
  }

  // ---------- Final fallback: Emergent storage ----------
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return { path: data.path, media_type: data.media_type };
}
